/**
 * advance-missiles.ts — v3M28 missile-as-persistent-entity machinery.
 *
 * Two surfaces:
 *
 *   - `enqueueMissile` — called from `fireSalvo` when the dispatched
 *     weapon's `subType === 'Missile'`. Allocates a `Missile` entity,
 *     computes the Bezier control point + accuracy roll via
 *     `state.rng` (so save round-trips and same-seed replays produce
 *     identical trajectories), and inserts it into `state.missiles`.
 *     Damage is NOT applied here — it's deferred to the impact tick.
 *
 *   - `advanceMissiles` — per-tick pass that walks `state.missiles`,
 *     bumps `ageTicks`, validates each missile's target, resolves
 *     damage on arrival, emits DamageEvents + impact VFX, and
 *     despawns finished / orphaned entries.
 *
 * Both functions are pure of side effects beyond `state` mutation —
 * no DOM access, no audio access, no console output. The audio
 * engine reads the missile diff each frame on its own; render layer
 * reads `state.missiles` each frame for sprite draw.
 *
 * Design lock: `docs/MISSILES.md`.
 */

import { nextFloat } from '../state/rng';
import { hitChance } from './combat';
import type {
  DamageEvent,
  GameState,
  Missile,
  ShipInstance,
  ShipTemplate,
  WeaponDef,
} from '../state/types';
import { damageTypeModifiers } from './combat-balance';
import { effectiveArmor, effectiveMaxHull } from './research-effects';
import { areTeammates } from './teams';
import {
  SQUADRON_FIGHTER_COUNT,
  updateSquadronFighterCount,
} from './balance-carrier-squadrons';

/**
 * Resolve a ship's template via the `${raceId}:${chassis}` key
 * convention. Private to this module — the same helper exists
 * inlined in `advance-combat.ts` / `advance-bombardment.ts` /
 * `advance-repair-yard.ts`; v3M28 keeps the duplication rather than
 * hoisting to a shared helper because the duplicates are 3 lines and
 * hoisting would touch four files needlessly.
 */
function getTemplate(state: GameState, ship: ShipInstance): ShipTemplate | undefined {
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return undefined;
  return state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Per-family travel time in ticks. Identical within a family — per-Mk
 * differentiation lives in damage / range / accuracy, not TTL.
 *
 * Per `MISSILES.md §6`:
 *   - Pulson Missile Mk 1-3 → 30 ticks (3.0 s @ 10 Hz)
 *   - Zeon Missile Mk 4-6   → 40 ticks (4.0 s @ 10 Hz)
 *
 * Unrecognised weapon names default to the Pulson family via
 * `missileFamilyFromWeaponName` (and therefore the Pulson TTL) —
 * defensive fallback so a future Concussion-Missile weapon that
 * hasn't been added to the family table here still flies for a
 * sensible duration rather than crashing or sticking at infinity.
 */
export const PULSON_TTL_TICKS = 30;
export const ZEON_TTL_TICKS = 40;

/** Bezier control-point offset magnitude as a fraction of path length.
 *  Per `MISSILES.md §4.1`. Multiplied by the `state.rng`-rolled
 *  scatter factor `s ∈ [-1.5, 1.5]`. */
const ARC_DEPTH = 0.15;

/** Range of the per-missile scatter factor. The sign determines arc
 *  direction (above vs below the direct line); the magnitude
 *  determines arc depth. */
const SCATTER_MIN = -1.5;
const SCATTER_MAX = 1.5;

// ---------------------------------------------------------------------------
// Family + TTL lookup
// ---------------------------------------------------------------------------

/**
 * Resolve the missile family from a weapon name. Family drives TTL
 * (per §6) and per-family rendering / SFX (per §9–§10).
 *
 * Pulson Missile Mk 1-3 → 'pulson'; Zeon Missile Mk 4-6 → 'zeon'.
 * Unknown names default to 'pulson' so the missile still flies for
 * a sensible duration — see DEFAULT_TTL_TICKS.
 */
export function missileFamilyFromWeaponName(
  weaponName: string,
): 'pulson' | 'zeon' {
  if (weaponName.startsWith('Pulson')) return 'pulson';
  if (weaponName.startsWith('Zeon')) return 'zeon';
  return 'pulson';
}

/** TTL in ticks for a missile of the given family. */
export function missileTtlForFamily(family: 'pulson' | 'zeon'): number {
  return family === 'zeon' ? ZEON_TTL_TICKS : PULSON_TTL_TICKS;
}

// ---------------------------------------------------------------------------
// Bezier geometry
// ---------------------------------------------------------------------------

/**
 * Three-phase easing curve mapping `ageFrac ∈ [0, 1]` to eased `t ∈
 * [0, 1]` per `MISSILES.md §4.2`. Slow launch (0-15%, quad-ease-out
 * to ~7.5%), linear cruise (15-85%, 7.5% → 92.5%), slight terminal
 * deceleration (85-100%, 92.5% → 100%).
 *
 * Exported for testing — the advance pass uses this every tick to
 * recompute each in-flight missile's position from its Bezier
 * triple.
 */
export function easeMissile(ageFrac: number): number {
  const f = Math.max(0, Math.min(1, ageFrac));
  if (f < 0.15) {
    const local = f / 0.15;
    return local * local * 0.075;
  }
  if (f < 0.85) {
    const local = (f - 0.15) / 0.7;
    return 0.075 + local * 0.85;
  }
  const local = (f - 0.85) / 0.15;
  return 0.925 + local * 0.075;
}

/**
 * Evaluate the quadratic Bezier at parameter `t`. Returns
 * `{ x, y }` for `P0 = (sx0, sy0)`, `P1 = (sx1, sy1)`, `Pc = (cx, cy)`
 * per `MISSILES.md §4`.
 *
 * `t` here is the EASED parameter (output of `easeMissile`), not
 * the raw `ageFrac`. Callers should ease first.
 */
export function bezierAt(
  missile: Missile,
  t: number,
): { x: number; y: number } {
  const omt = 1 - t;
  const x =
    omt * omt * missile.sx0 +
    2 * omt * t * missile.cx +
    t * t * missile.sx1;
  const y =
    omt * omt * missile.sy0 +
    2 * omt * t * missile.cy +
    t * t * missile.sy1;
  return { x, y };
}

/**
 * Tangent of the quadratic Bezier at parameter `t` — used by the
 * renderer to rotate the missile sprite into its direction of
 * travel. Returns the raw (not-unit-normalised) tangent vector;
 * caller normalises if needed.
 */
export function bezierTangentAt(
  missile: Missile,
  t: number,
): { x: number; y: number } {
  const omt = 1 - t;
  const x =
    2 * omt * (missile.cx - missile.sx0) +
    2 * t * (missile.sx1 - missile.cx);
  const y =
    2 * omt * (missile.cy - missile.sy0) +
    2 * t * (missile.sy1 - missile.cy);
  return { x, y };
}

/**
 * Current eased position of an in-flight missile at game tick `tick`.
 * Convenience wrapper bundling the age-derivation + ease + Bezier
 * evaluation steps. Returns the spawn point at `tick === launchedAtTick`
 * and the target point at `tick >= launchedAtTick + ttlTicks`.
 */
export function missilePositionAt(
  missile: Missile,
  tick: number,
): { x: number; y: number } {
  const ageFrac = (tick - missile.launchedAtTick) / missile.ttlTicks;
  return bezierAt(missile, easeMissile(ageFrac));
}

// ---------------------------------------------------------------------------
// Launch — invoked from fireSalvo for Missile-subType weapons
// ---------------------------------------------------------------------------

/**
 * Push one Missile entity onto `state.missiles`. Bezier scatter is
 * rolled via `state.rng` so save round-trips preserve trajectories.
 * Accuracy is also rolled at fire-time (`willHit` frozen here, not
 * at impact) — matches the existing accuracy-rolled-at-fire pattern
 * in every other weapon's instant-damage path.
 *
 * Caller is responsible for calling this exactly `count` times per
 * salvo (the count comes from the launching ship's per-slot weapon
 * loadout). Each invocation rolls its own scatter + accuracy.
 *
 * No damage applied here — that's deferred to the impact tick in
 * `advanceMissiles`.
 */
export function enqueueMissile(
  state: GameState,
  attacker: ShipInstance,
  target: ShipInstance,
  weapon: WeaponDef,
): void {
  // Bezier endpoints frozen at fire-time. The missile does NOT chase
  // a moving target — target movement during flight may cause a
  // visual miss even with `willHit === true`.
  const sx0 = attacker.sx;
  const sy0 = attacker.sy;
  const sx1 = target.sx;
  const sy1 = target.sy;

  // Perpendicular-offset midpoint, scaled by RNG-rolled scatter.
  const dx = sx1 - sx0;
  const dy = sy1 - sy0;
  const len = Math.hypot(dx, dy);
  // Unit perpendicular (90° CCW). Falls back to (0, 1) when launcher
  // and target are coincident (degenerate; shouldn't happen, but no
  // crash on edge cases).
  const ux = len > 0 ? -dy / len : 0;
  const uy = len > 0 ? dx / len : 1;
  const scatter = SCATTER_MIN + nextFloat(state.rng) * (SCATTER_MAX - SCATTER_MIN);
  const cx = (sx0 + sx1) / 2 + ux * len * ARC_DEPTH * scatter;
  const cy = (sy0 + sy1) / 2 + uy * len * ARC_DEPTH * scatter;

  // Accuracy roll frozen at fire-time. Uses the same hitChance math
  // as the instant-damage path — accuracy × (1 - dodge), rolled
  // against a uniform [0, 1).
  const targetTmpl = getTemplate(state, target);
  const dodge = targetTmpl?.dodge ?? 0;
  const hitP = hitChance(weapon.accuracy, dodge);
  const accRoll = nextFloat(state.rng);
  const willHit = accRoll < hitP;

  const family = missileFamilyFromWeaponName(weapon.name);
  const ttlTicks = missileTtlForFamily(family);

  const id = state.nextEntityId;
  state.nextEntityId = id + 1;
  state.missiles.set(id, {
    id,
    ownerEmpireId: attacker.ownerEmpireId,
    launchedFromShipId: attacker.id,
    systemId: attacker.systemId ?? target.systemId ?? 0,
    targetShipId: target.id,
    weaponName: weapon.name,
    launchedAtTick: state.tick,
    ttlTicks,
    sx0,
    sy0,
    sx1,
    sy1,
    cx,
    cy,
    willHit,
  });
}

// ---------------------------------------------------------------------------
// Per-tick advance pass — lands in v3M28.3
// ---------------------------------------------------------------------------

/**
 * v3M28.3 — walk every entry in `state.missiles`, age them, validate
 * their targets, resolve impact on arrival, despawn finished entries.
 *
 * Iteration is in ascending id order for determinism (Map preserves
 * insertion order — but same-tick saves loaded into a fresh process
 * would replay in the same order, so explicit sort keeps things
 * predictable independent of Map internals). Despawns mutate the
 * Map during iteration; that's safe since we've materialised the id
 * list up front.
 *
 * Pipeline position: runs AFTER `advanceCombat` (so newly-launched
 * missiles get one tick of "age 0 in flight" before potentially
 * arriving) and BEFORE `advanceShipMovement` (so impacts apply to
 * the ship's pre-movement position — the same convention
 * `advanceSurfaceBatteries` uses).
 */
export function advanceMissiles(state: GameState): void {
  const ids = [...state.missiles.keys()].sort((a, b) => a - b);
  for (const id of ids) {
    const missile = state.missiles.get(id);
    if (!missile) continue;
    const ageTicks = state.tick - missile.launchedAtTick;
    if (ageTicks < missile.ttlTicks) continue; // still in flight

    // ── Arrival ──────────────────────────────────────────────────
    // Target validation per `MISSILES.md §7`:
    //   - missing (`state.ships.get` returns undefined) → despawn.
    //   - dead (currentHull <= 0)                      → despawn.
    //   - left the system (different systemId)         → despawn.
    //   - mid-jump (systemId === null)                 → despawn.
    // Despawn is silent — no VFX, no SFX, no damage. Re-target is a
    // v4 escalation per the design lock.
    const target = state.ships.get(missile.targetShipId);
    if (
      !target ||
      target.currentHull <= 0 ||
      target.systemId !== missile.systemId
    ) {
      state.missiles.delete(id);
      continue;
    }
    // v4M3.3 — friendly-fire block per `TEAMS.md §4` point 3.
    // Defensive belt-and-braces: in v4 teams are locked at game start
    // so a missile's target can't become a teammate mid-flight, but
    // a v5+ mid-game team-reassign mechanic would surface this edge.
    // pickTarget already filters teammates so the despawn-silent
    // path is rare-to-impossible today; the check costs ~1 Map lookup.
    if (areTeammates(state, missile.ownerEmpireId, target.ownerEmpireId)) {
      state.missiles.delete(id);
      continue;
    }

    // Resolve weapon from the live catalogue. Defensive null check
    // for the unlikely case the weapon name was retired mid-flight
    // (catalogue is read-only in v1 but the check costs nothing).
    const weapon = state.weapons.get(missile.weaponName);
    if (!weapon) {
      state.missiles.delete(id);
      continue;
    }

    // Miss outcome — `willHit` was rolled at fire-time. Still emit
    // the detonation VFX so the player sees the missile "fizzle" at
    // arrival. v3M28.4 lands the detonation VFX wiring; for now the
    // miss path is the same silent despawn as the hit path's
    // post-impact cleanup. The DamageEvent is suppressed because no
    // damage applied.
    if (!missile.willHit) {
      state.missiles.delete(id);
      continue;
    }

    // ── Hit outcome — apply damage via the existing chain ────────
    const targetTmpl = getTemplate(state, target);
    const targetEmpire = state.empires.get(target.ownerEmpireId);
    const armor =
      targetTmpl && targetEmpire
        ? effectiveArmor(state, targetEmpire, targetTmpl)
        : targetTmpl?.armor ?? 0;
    const armorFactor = Math.max(0, Math.min(1, armor));
    const mods = damageTypeModifiers(state.balanceData)[weapon.damageType];

    const rawShieldHit = Math.min(weapon.damage, target.currentShields);
    const rawHullHit = Math.max(0, weapon.damage - rawShieldHit);
    const scaledShield = rawShieldHit * mods.shield;
    const scaledPostShield = rawHullHit * mods.hull;
    const hullDamage = scaledPostShield * (1 - armorFactor);

    target.currentShields = Math.max(0, target.currentShields - scaledShield);
    target.currentHull = Math.max(0, target.currentHull - hullDamage);

    // v3M17.3 squadron variable-count HP pool — same recompute as
    // the ship-vs-ship damage chain in `advance-combat.ts`. Updates
    // `liveFighterCount` so the renderer immediately reflects the
    // post-impact squadron strength.
    if (target.liveFighterCount !== undefined && targetTmpl && targetEmpire) {
      const fullPool = effectiveMaxHull(state, targetEmpire, targetTmpl);
      updateSquadronFighterCount(target, fullPool / SQUADRON_FIGHTER_COUNT);
    }

    // v3M19 DamageEvent for the floating-damage-numbers renderer.
    // Suppressed if the target is mid-jump (would float over nothing)
    // or if the damageType doesn't match the v3M19 union. Missiles
    // are Concussion sub-type so the union always matches; the EMP
    // guard is defensive against future weapon-type drift.
    if (
      target.systemId !== null &&
      (weapon.damageType === 'Energy' ||
        weapon.damageType === 'Kinetic' ||
        weapon.damageType === 'Concussion')
    ) {
      state.combatEvents.push({
        targetShipId: target.id,
        ownerEmpireId: missile.ownerEmpireId,
        systemId: target.systemId,
        sx: target.sx,
        sy: target.sy,
        shieldDamage: scaledShield,
        hullDamage,
        hitCount: 1,
        missCount: 0,
        damageType: weapon.damageType,
      });
    }

    state.missiles.delete(id);
  }
}

// ---------------------------------------------------------------------------
// Helpers (re-exported for tests / future consumers)
// ---------------------------------------------------------------------------

// `DamageEvent` is re-exported for the eventual impact-resolution
// path that emits floating damage numbers. The import lives here so
// the .3 implementation can drop in without re-touching imports.
export type { DamageEvent };

// Same for `damageTypeModifiers` + `effectiveArmor` — the impact
// resolution chain depends on both; declared here so .3 doesn't
// re-import them.
export { damageTypeModifiers, effectiveArmor };
